import { Component, effect, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';

import { BudgetsFilter } from '../../../../core/filters/budgets.filter';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { LocalStorageKeyConstants } from '../../../../core/constants/constants';
import { PropertyStatuses } from '../../../../core/enums/data.enums';
import { FinancialsPermission } from '../../../../core/enums/permissions.enums';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { ParentFocusDirective } from '../../../../core/directives/parent-focus.directive';
import { MultiSelectComponent } from '../../../../parts/multi-select/multi-select.component';
import { BaseSearchFilterComponent } from '../../../../core/base-classes/base-search-filter.component';

@Component({
  selector: 'ms-budget-search-filter',
  standalone: true,
  imports: [CommonModule, FormsModule, FaDuotoneIconComponent, ParentFocusDirective, MultiSelectComponent],
  templateUrl: './budget-search-filter.html'
})
export class BudgetSearchFilter extends BaseSearchFilterComponent<BudgetsFilter> implements OnInit {

  public selectStatusOptions: PropertyStatuses[] = [PropertyStatuses.Active, PropertyStatuses.Inactive];
  public selectedStatuses: PropertyStatuses[] = [];
  public canViewDeletedBudgets = false;

  public nameInput = '';
  public minimumYearInput?: number;
  public maximumYearInput?: number;

  constructor(
    public userSessionService: UserSessionService,
    private authorizationService: AuthorizationService,
    private authorizationStateService: AuthorizationStateService
  ) {
    super();
    // Reactively re-evaluate deleted-budget access when auth state or active org changes,
    // so the option doesn't go stale on org switch or once the auth state finishes loading.
    effect(() => {
      const authState = this.authorizationStateService.authorizationState();
      const activeOrgId = this.userSessionService.activeOrganization()?.organizationId;
      this.applyDeletedBudgetAccess(authState ?? null, activeOrgId);
    });
  }

  public ngOnInit(): void {
    this.selectedStatuses = [...(this.activeFilter.statuses || [])];
    this.minimumYearInput = this.activeFilter.minimumYear;
    this.maximumYearInput = this.activeFilter.maximumYear;
    this.onApply();
  }

  private applyDeletedBudgetAccess(
    authState: ReturnType<AuthorizationStateService['authorizationState']> | null,
    activeOrgId: number | undefined
  ): void {
    this.canViewDeletedBudgets = authState
      ? this.authorizationService.hasPermission(authState, FinancialsPermission.ReadDeleted, activeOrgId)
      : false;

    if (this.canViewDeletedBudgets) {
      this.selectStatusOptions = [PropertyStatuses.Active, PropertyStatuses.Inactive, PropertyStatuses.Deleted];
      this.activeFilter.includeDeletedBudgets = true;
    } else {
      this.selectStatusOptions = [PropertyStatuses.Active, PropertyStatuses.Inactive];
      this.activeFilter.includeDeletedBudgets = false;

      // Remove Deleted from any persisted filter statuses
      if (this.activeFilter.statuses?.includes(PropertyStatuses.Deleted)) {
        this.activeFilter.statuses = this.activeFilter.statuses.filter(s => s !== PropertyStatuses.Deleted);
      }
    }
  }

  public override get activeFilter(): BudgetsFilter {
    return this.userSessionService.budgetsFilter;
  }

  protected override getDefaultFilter(): BudgetsFilter {
    return ListConstants.BUDGETS_FILTER_DEFAULT();
  }

  protected override get preservedFilterKeys(): (keyof BudgetsFilter)[] {
    return ['organizationIds'];
  }

  protected override get dropdownSelector(): string {
    return '#budget-search-filter-dropdown';
  }

  protected override applyAuthGatedFields(): void {
    const authState = this.authorizationStateService.authorizationState();
    const activeOrgId = this.userSessionService.activeOrganization()?.organizationId;
    this.applyDeletedBudgetAccess(authState ?? null, activeOrgId);
  }

  protected override syncStateFromFilter(): void {
    this.minimumYearInput = undefined;
    this.maximumYearInput = undefined;
    this.selectedStatuses = [...(this.activeFilter.statuses || [])];
  }

  //#region Tag Inputs

  public addName(): void {
    if (this.nameInput && this.nameInput.trim() !== '') {
      this.activeFilter.names ??= [];
      this.activeFilter.names.push(this.nameInput.trim());
      this.saveToStorage();
    }
    this.nameInput = '';
  }

  public removeName(index: number): void {
    this.activeFilter.names?.splice(index, 1);
    this.saveToStorage();
  }

  //#endregion Tag Inputs

  //#region Range & Multi-select

  public onMinYearChange(): void {
    this.activeFilter.minimumYear = this.minimumYearInput == null ? undefined : Number(this.minimumYearInput);
    this.saveToStorage();
  }

  public onMaxYearChange(): void {
    this.activeFilter.maximumYear = this.maximumYearInput == null ? undefined : Number(this.maximumYearInput);
    this.saveToStorage();
  }

  public onStatusChange(event: PropertyStatuses[]): void {
    this.activeFilter.statuses = event;
    this.saveToStorage();
  }

  //#endregion Range & Multi-select

  //#region Helpers

  public override setFilterCount(): void {
    this.filterCount =
      (this.activeFilter.names?.length || 0) +
      (this.activeFilter.statuses?.length || 0) +
      (this.activeFilter.minimumYear != null ? 1 : 0) +
      (this.activeFilter.maximumYear != null ? 1 : 0);
  }

  protected override saveToStorage(): void {
    this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.BUDGETS_FILTER(), this.activeFilter);
  }

  //#endregion Helpers
}
